import { Link } from 'react-router-dom';
import { beverages } from '../../../mocks/beverages';
import { useCart } from '../../../contexts/CartContext';
import { useToast } from '../../../contexts/ToastContext';

interface RelatedProductsProps {
  currentId: string;
  category: string;
}

export default function RelatedProducts({ currentId, category }: RelatedProductsProps) {
  const { addItem } = useCart();
  const toast = useToast();

  const related = beverages
    .filter((b) => b.category === category && b.id !== currentId)
    .slice(0, 4);

  if (related.length === 0) return null;

  const formatPrice = (p: number) => `GYD $${p.toLocaleString()}`;

  const handleAdd = (bev: typeof beverages[0], e: React.MouseEvent) => {
    e.preventDefault();
    addItem({ id: bev.id, name: bev.name, price: bev.price, image: bev.image, volume: bev.volume });
    toast.cart(`${bev.name} added!`, `GYD $${bev.price.toLocaleString()} · ${bev.volume}`);
  };

  return (
    <section className="mt-20 pt-12 border-t border-gray-100">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-bold text-[#FF6B35] uppercase tracking-widest mb-1">You Might Also Like</p>
          <h3
            className="text-3xl font-black text-gray-900"
            style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.02em' }}
          >
            More <span className="text-[#FF6B35]">/</span> {category.charAt(0).toUpperCase() + category.slice(1)}
          </h3>
        </div>
        <Link
          to="/menu"
          className="text-sm font-medium text-gray-500 hover:text-[#FF6B35] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
        >
          View all
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line text-sm" />
          </div>
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        {related.map((bev) => (
          <Link
            key={bev.id}
            to={`/beverage/${bev.id}`}
            className="bg-white rounded-2xl overflow-hidden group hover:scale-[1.02] transition-transform duration-300 block"
          >
            <div className="relative h-48 bg-gradient-to-br from-amber-50 to-orange-50 overflow-hidden">
              <img
                src={bev.image}
                alt={bev.name}
                className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
              />
              {bev.badge && (
                <span className="absolute top-3 left-3 bg-[#FF6B35] text-white text-xs font-bold px-2 py-1 rounded-lg">
                  {bev.badge}
                </span>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-1 mb-1">
                <div className="w-4 h-4 flex items-center justify-center text-amber-400">
                  <i className="ri-star-fill text-xs" />
                </div>
                <span className="text-xs font-semibold text-gray-700">{bev.rating}</span>
                <span className="text-xs text-gray-400">({bev.reviews})</span>
              </div>
              <h4 className="font-bold text-gray-900 text-sm mb-0.5 truncate">{bev.name}</h4>
              <p className="text-gray-400 text-xs mb-3">{bev.volume}</p>
              <div className="flex items-center justify-between">
                <span className="font-black text-gray-900 text-sm">{formatPrice(bev.price)}</span>
                <button
                  onClick={(e) => handleAdd(bev, e)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-[#FF6B35] hover:bg-[#e85d2a] text-white transition-all cursor-pointer"
                >
                  <i className="ri-add-line text-sm" />
                </button>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
